<template>
   <div
      class="supplies-project-percentage-input"
      :data-grid-section="gridSection"
      :data-grid-table="gridTableIndex"
      :data-grid-row="gridRowIndex"
      :data-grid-col="gridColIndex"
   >
      <FieldInput
         ref="input"
         style="min-width: 6rem; width: 100%; font-size: 12px !important"
         class="mb-0"
         :ident="id"
         append="%"
         :field="displayProjectPercentageField"
         :has-warning="hasWarning"
         :placeholder="placeholder"
         :disabled="disabled"
         fill
         @input="onInput"
         @blur="onBlur"
      />
   </div>
</template>

<script>
import {integer} from 'vuelidate/lib/validators';
import FieldInput from '@/components/forms/FieldInput';
import {hasValueChanged, normalizeNullableInteger} from '@/helpers/expenseInput';

export default {
   components: {
      FieldInput,
   },

   props: {
      gridSection: {type: String, required: false},
      gridTableIndex: {type: Number, required: false},
      gridRowIndex: {type: Number, required: false},
      gridColIndex: {type: Number, required: false},
      ident: {
         type: [String, Number],
      },
      value: {
         type: Number,
         default: null,
      },
      assigneeId: {
         type: String,
         default: '',
      },
      projectId: {
         type: [String, Number],
         default: null,
      },
      summary: {
         type: Object,
         default: null,
      },
      placeholder: {
         type: String,
         default: null,
      },
      disabled: {
         type: Boolean,
         default: false,
      },
      hasWarning: {
         type: Boolean,
         default: false,
      },
      projectIds: {
         type: Array,
         default: () => [],
      },
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         draftProjectPercentage: null,
         hasDraftProjectPercentage: false,
         startingProjectPercentage: null,
      };
   },

   computed: {
      id() {
         return `input-percentage-${this.ident}`.replace(/\s/g, '-');
      },

      projectPercentageField() {
         const summaryValue =
            this.summary && this.projectId
               ? this.summary.projectsData?.[this.projectId] ?? this.value
               : this.value;
         return {
            value: summaryValue,
            status: '',
            lastUpdated: '',
         };
      },

      displayProjectPercentageField() {
         return {
            ...this.projectPercentageField,
            value: this.hasDraftProjectPercentage
               ? this.draftProjectPercentage
               : this.projectPercentageField.value,
         };
      },

      persistedProjectValue() {
         if (!this.summary || !this.projectId) return this.value;
         return this.summary.projectsData?.[this.projectId] ?? this.value;
      },
   },

   methods: {
      normalizeInputValue(value) {
         return normalizeNullableInteger(value);
      },

      onInput(newValue) {
         if (!this.hasDraftProjectPercentage) {
            this.startingProjectPercentage = this.normalizeInputValue(this.persistedProjectValue);
         }
         this.hasDraftProjectPercentage = true;
         this.draftProjectPercentage = newValue;
         this.validate(newValue);
      },

      async onBlur(newValue) {
         const normalizedValue = this.normalizeInputValue(newValue);
         const startingValue = this.hasDraftProjectPercentage
            ? this.startingProjectPercentage
            : this.normalizeInputValue(this.persistedProjectValue);

         if (!hasValueChanged(normalizedValue, startingValue)) {
            this.draftProjectPercentage = normalizedValue;
            this.hasDraftProjectPercentage = false;
            this.startingProjectPercentage = null;
            return;
         }

         await this.setProjectPercentage(newValue);
      },

      async setProjectPercentage(newValue) {
         if (!this.validate(newValue)) return;

         newValue = this.normalizeInputValue(newValue);

         this.$emit('input', newValue);
         this.draftProjectPercentage = newValue;
         this.hasDraftProjectPercentage = false;
         this.startingProjectPercentage = null;

         try {
            await this.$store.dispatch(`${this.expenseType}/saveProjectPercentage`, {
               companyId: this.$companyId,
               summary: {
                  ...this.summary,
                  assigneeId: this.assigneeId,
               },
               projectId: this.projectId,
               value: newValue,
            });
         } catch {
            // The store action handles rollback and user alerting.
         }
      },

      /** Validate a percentage */
      validate(value) {
         if (value === '' || value === null) {
            this.$refs.input.setState(null);
            return true;
         }
         if (!integer(value)) {
            this.$refs.input.setState(false, 'Must be an integer');
            return false;
         }
         value = parseInt(value, 10);
         if (value < 0 || value > 100) {
            this.$refs.input.setState(false, 'Must be between 0 and 100');
            return false;
         }
         this.$refs.input.setState(null);
         return true;
      },
   },
};
</script>

<style lang="scss" scoped></style>
